import { h } from '../lib/dom.js';
import { getState, fmtNum, filteredTransactions } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  setTxnFilter, setTxnAccountFilter, setTxnTimeFilter, setTxnSort, setTxnSearch, toggleExpand, exportCSV, exportPDF,
  setTagDraft, addTxnTag, removeTxnTag
} from '../actions.js';

function pill(label, active, onclick) {
  return h('div', { class: 'pill' + (active ? ' active' : ''), onclick }, label);
}

function tagChip(t, tag, removable) {
  return h('span', { class: 'tag-chip' }, [
    h('span', {}, tag),
    removable ? h('button', {
      class: 'tag-chip-remove', 'aria-label': `Remove tag ${tag}`,
      onclick: (e) => { e.stopPropagation(); removeTxnTag(t.id, tag); }
    }, h('span', { html: iconMarkup('x', { size: 10 }) })) : null
  ]);
}

// Editing only happens once a row is expanded — tapping the collapsed row's
// read-only chips (tagChip above, removable: false) still just toggles it
// open, same as tapping anywhere else on the row.
function tagEditor(s, t) {
  const tags = s.txnTags[t.id] || [];
  return h('div', { class: 'tag-editor', onclick: (e) => e.stopPropagation() }, [
    tags.length ? h('div', { class: 'tag-chip-row' }, tags.map((tag) => tagChip(t, tag, true))) : null,
    h('div', { class: 'field-row' }, [
      h('input', {
        placeholder: 'Add tag…', value: s.tagDraft,
        'data-preserve-focus': 'tag-draft',
        oninput: (e) => setTagDraft(e.target.value),
        onkeydown: (e) => { if (e.key === 'Enter') addTxnTag(t.id); }
      }),
      h('button', { class: 'btn btn-secondary btn-sm', onclick: () => addTxnTag(t.id) }, 'Add')
    ])
  ]);
}

export function renderActivity() {
  const s = getState();
  const txnFiltered = filteredTransactions(s);

  const accountSelect = h('select', { onchange: (e) => setTxnAccountFilter(e.target.value) }, [
    h('option', { value: 'all', selected: s.txnAccountFilter === 'all' || undefined }, 'All accounts'),
    ...s.wallets.map((a) => h('option', { value: a.id, selected: a.id === s.txnAccountFilter || undefined }, a.name))
  ]);

  const sortSelect = h('select', { onchange: (e) => setTxnSort(e.target.value) }, [
    h('option', { value: 'date-desc', selected: s.txnSort === 'date-desc' || undefined }, 'Newest first'),
    h('option', { value: 'date-asc', selected: s.txnSort === 'date-asc' || undefined }, 'Oldest first'),
    h('option', { value: 'amount-desc', selected: s.txnSort === 'amount-desc' || undefined }, 'Amount (high–low)'),
    h('option', { value: 'amount-asc', selected: s.txnSort === 'amount-asc' || undefined }, 'Amount (low–high)')
  ]);

  const rows = txnFiltered.map((t) => {
    const amountFmt = (t.amount < 0 ? '-' : '+') + fmtNum(Math.abs(t.amount)) + ' ' + t.ccy;
    const expanded = s.expandedTxId === t.id;
    const tags = s.txnTags[t.id] || [];
    const children = [
      h('div', { class: 'tx-row' }, [
        h('div', {}, [
          h('div', { class: 'desc' }, t.desc),
          h('div', { class: 'meta flex-gap-sm items-center' }, [
            h('span', {}, `${t.date} · ${t.wallet}`),
            tags.length && !expanded ? h('div', { class: 'tag-chip-row' }, tags.map((tag) => tagChip(t, tag, false))) : null
          ])
        ]),
        h('span', { class: 'amount ' + (t.amount > 0 ? 'positive' : 'negative') }, amountFmt)
      ])
    ];
    if (expanded) {
      children.push(h('div', { class: 'tx-detail' }, [
        h('div', { class: 'tx-detail-row' }, [
          h('span', {}, `Hash ${t.hash}`),
          h('a', { href: `https://explorer.tynti.app/tx/${t.hash}`, target: '_blank', rel: 'noopener noreferrer' }, 'View on chain ↗')
        ]),
        t.type === 'fx' && t.fxRate
          ? h('div', { class: 'tx-detail-row' }, h('span', {}, `FX rate: 1 ${t.ccy} = ${t.fxRate} ${s.reportingCurrency}`))
          : null
      ]));
      children.push(tagEditor(s, t));
    }
    return h('div', { class: 'tx-row-wrap', onclick: () => toggleExpand(t.id) }, children);
  });

  return h('div', { class: 'screen' }, [
    h('div', { class: 'flex-between' }, [
      h('h1', { class: 'screen-title' }, 'Activity'),
      h('div', { class: 'flex-gap-sm' }, [
        h('div', { class: 'icon-btn', onclick: exportPDF }, [h('span', { html: iconMarkup('filePdf', { size: 14 }) }), 'PDF']),
        h('div', { class: 'icon-btn', onclick: exportCSV }, [h('span', { html: iconMarkup('fileCsv', { size: 14 }) }), 'CSV'])
      ])
    ]),
    h('input', {
      placeholder: 'Search description, account, or hash…', value: s.txnSearch,
      'data-preserve-focus': 'txn-search',
      oninput: (e) => setTxnSearch(e.target.value)
    }),
    h('div', { class: 'flex-gap-sm select-row' }, [accountSelect, sortSelect]),
    h('div', { class: 'pill-row' }, [
      pill('All', s.txnFilter === 'all', () => setTxnFilter('all')),
      pill('In', s.txnFilter === 'in', () => setTxnFilter('in')),
      pill('Out', s.txnFilter === 'out', () => setTxnFilter('out')),
      pill('All time', s.txnTimeFilter === 'all', () => setTxnTimeFilter('all')),
      pill('1M', s.txnTimeFilter === '1m', () => setTxnTimeFilter('1m')),
      pill('12M', s.txnTimeFilter === '12m', () => setTxnTimeFilter('12m'))
    ]),
    rows.length
      ? h('div', { class: 'tx-list' }, rows)
      : h('div', { class: 'empty-state' }, s.transactions.length ? 'No transactions match these filters.' : 'No activity yet.')
  ]);
}
